import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PhoneButton from '@/components/base/PhoneButton';
import { bolgeData } from '@/mocks/bolge-data';

export default function HizmetBolgeleriPage() {
  const [activeArea, setActiveArea] = useState(bolgeData[0].slug);
  const current = bolgeData.find((a) => a.slug === activeArea) ?? bolgeData[0];

  useEffect(() => {
    document.title = 'Çilingir Hizmet Bölgeleri | Beylikdüzü, Esenyurt, Büyükçekmece, Avcılar, Bahçeşehir - Alfa Çilingir';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Alfa Çilingir hizmet bölgeleri: Beylikdüzü, Esenyurt, Büyükçekmece, Avcılar ve Bahçeşehir\'in tüm mahallelerinde 7/24 profesyonel çilingir hizmeti. Acil kapı açma, kilit değiştirme.');

    const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `${siteUrl}/hizmet-bolgeleri`;

    const existingScript = document.getElementById('schema-hizmet-bolgeleri');
    if (existingScript) existingScript.remove();

    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': `${siteUrl}/hizmet-bolgeleri#webpage`,
          url: `${siteUrl}/hizmet-bolgeleri`,
          name: 'Çilingir Hizmet Bölgeleri | Alfa Çilingir',
          description: 'Beylikdüzü, Esenyurt, Büyükçekmece ve Bahçeşehir\'de 7/24 çilingir hizmeti.',
          inLanguage: 'tr-TR',
        },
        {
          '@type': 'ItemList',
          name: 'Alfa Çilingir Hizmet Bölgeleri',
          itemListElement: bolgeData.map((b, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: `${b.name} Çilingir`,
            url: `${siteUrl}/${b.slug}`,
          })),
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: siteUrl },
            { '@type': 'ListItem', position: 2, name: 'Hizmet Bölgeleri', item: `${siteUrl}/hizmet-bolgeleri` },
          ],
        },
      ],
    };

    const script = document.createElement('script');
    script.id = 'schema-hizmet-bolgeleri';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('schema-hizmet-bolgeleri');
      if (s) s.remove();
      const c = document.querySelector('link[rel="canonical"]');
      if (c) c.remove();
    };
  }, []);

  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-16 bg-gray-900 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Istanbul%20Bosphorus%20aerial%20view%2C%20European%20side%20districts%2C%20urban%20landscape%2C%20Turkey%2C%20wide%20panoramic%20view%2C%20daytime&width=1920&height=500&seq=bpage001&orientation=landscape"
              alt="İstanbul Çilingir Hizmet Bölgeleri"
              className="w-full h-full object-cover object-top opacity-30"
            />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 text-center">
            <span className="inline-flex items-center gap-2 text-orange-400 text-xs font-bold uppercase tracking-widest mb-4">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-map-pin-line"></i></span>
              Hizmet Bölgelerimiz
            </span>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4">
              Çilingir Hizmet Bölgelerimiz
            </h1>
            <p className="text-gray-300 max-w-2xl mx-auto text-base md:text-lg mb-8">
              İstanbul Avrupa Yakası'nda Beylikdüzü, Esenyurt, Büyükçekmece, Avcılar ve Bahçeşehir'in tüm mahalle ve semtlerinde 7/24 çilingir hizmeti.
            </p>
            <PhoneButton size="lg" />
          </div>
        </section>

        {/* Bölge Kartları */}
        <section className="py-14 bg-stone-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">Hizmet Verdiğimiz Bölgeler</h2>
              <p className="text-gray-400 text-sm mt-2">Her bölgeye tıklayarak mahalle detaylarını görebilirsiniz.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
              {bolgeData.map((bolge) => (
                <Link
                  key={bolge.slug}
                  to={`/${bolge.slug}`}
                  className="group relative rounded-2xl overflow-hidden cursor-pointer block"
                >
                  <div className="w-full h-52">
                    <img
                      src={bolge.img}
                      alt={`${bolge.name} Çilingir`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent group-hover:from-orange-900/80 transition-all duration-300"></div>
                  <div className="absolute inset-0 p-5 flex flex-col justify-end">
                    <h3 className="text-white font-extrabold text-xl mb-1">{bolge.name}</h3>
                    <p className="text-white/70 text-xs mb-2">{bolge.desc}</p>
                    <span className="inline-flex items-center gap-1 text-orange-300 text-xs font-semibold">
                      Detayları Gör
                      <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line"></i></span>
                    </span>
                  </div>
                </Link>
              ))}
            </div>

            {/* Tab Detail */}
            <div className="flex flex-wrap gap-3 mb-8 justify-center">
              {bolgeData.map((area) => (
                <button
                  key={area.slug}
                  onClick={() => setActiveArea(area.slug)}
                  className={`px-6 py-3 rounded-full font-semibold text-sm whitespace-nowrap cursor-pointer transition-all duration-200 ${
                    activeArea === area.slug
                      ? 'bg-orange-500 text-white'
                      : 'bg-white text-gray-600 border border-stone-200 hover:border-orange-300'
                  }`}
                >
                  {area.name}
                </button>
              ))}
            </div>

            {/* Active Area Detail */}
            <div className="bg-white rounded-2xl overflow-hidden border border-stone-100">
              <div className="flex flex-col lg:flex-row">
                <div className="lg:w-2/5 h-64 lg:h-auto">
                  <img
                    src={current.img}
                    alt={current.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="lg:w-3/5 p-6 md:p-10">
                  <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-2">{current.title}</h2>
                  <p className="text-gray-500 text-sm leading-relaxed mb-5">{current.longDesc}</p>

                  <h3 className="font-bold text-gray-800 text-xs uppercase tracking-wider mb-3">Hizmet Verdiğimiz Mahalleler</h3>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {current.mahalleler.map((m) => (
                      <Link
                        key={m.slug}
                        to={`/${current.slug}/${m.slug}`}
                        className="bg-orange-50 hover:bg-orange-100 text-orange-700 text-xs px-3 py-1.5 rounded-full border border-orange-100 hover:border-orange-300 transition-all cursor-pointer"
                      >
                        {m.name}
                      </Link>
                    ))}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link
                      to={`/${current.slug}`}
                      className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-3 rounded-full whitespace-nowrap transition-all duration-200 cursor-pointer text-sm"
                    >
                      <span className="w-4 h-4 flex items-center justify-center"><i className="ri-map-pin-line"></i></span>
                      {current.name} Detay Sayfası
                    </Link>
                    <a
                      href="tel:05416247982"
                      className="inline-flex items-center gap-2 border-2 border-orange-500 text-orange-500 hover:bg-orange-50 font-bold px-6 py-3 rounded-full whitespace-nowrap transition-all duration-200 cursor-pointer text-sm"
                    >
                      <span className="w-4 h-4 flex items-center justify-center"><i className="ri-phone-fill"></i></span>
                      Hemen Ara
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Map Section */}
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <h2 className="text-2xl font-extrabold text-gray-900 mb-6 text-center">Hizmet Bölgesi Haritası</h2>
            <div className="rounded-2xl overflow-hidden border border-stone-100 h-96">
              <iframe
                title="Alfa Çilingir Hizmet Bölgeleri - Beylikdüzü, Esenyurt, Büyükçekmece, Bahçeşehir"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d96168.0!2d28.6!3d41.0!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14b55f5e5e5e5e5e%3A0x0!2sBeylikd%C3%BCz%C3%BC%2C+%C4%B0stanbul!5e0!3m2!1str!2str!4v1700000000000!5m2!1str!2str"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-orange-500">
          <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
            <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-3">Bölgenizde Çilingir mi Arıyorsunuz?</h2>
            <p className="text-orange-100 mb-6">Hemen arayın, 15 dakikada yanınızda olalım.</p>
            <a
              href="tel:05416247982"
              className="inline-flex items-center gap-3 bg-white text-orange-500 font-extrabold px-8 py-4 rounded-full whitespace-nowrap cursor-pointer hover:bg-orange-50 transition-all duration-200 text-lg"
            >
              <span className="w-6 h-6 flex items-center justify-center"><i className="ri-phone-fill text-xl"></i></span>
              0541 624 79 82
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
